import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Eye, MousePointerClick, AlertCircle, CheckCircle2,
  AlertTriangle, UserX, Pencil, Users, Clock,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useCampaign, useCampaignSends } from '@/hooks/useNewsletter';
import { CAMPAIGN_STATUS_LABELS, type CampaignStatus } from '@/types/newsletter';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';

const STATUS_TONES: Record<CampaignStatus, 'neutral' | 'info' | 'brass' | 'success' | 'warning' | 'danger'> = {
  draft:     'neutral',
  scheduled: 'info',
  sending:   'brass',
  sent:      'success',
  paused:    'warning',
  cancelled: 'neutral',
  failed:    'danger',
};

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const { data: campaign, isLoading, error } = useCampaign(id);
  const { data: sends } = useCampaignSends(id);

  const urlClicks = useMemo(() => {
    if (!sends) return new Map<string, number>();
    const m = new Map<string, number>();
    for (const s of sends) {
      for (const [url, count] of Object.entries(s.clickedUrls ?? {})) {
        m.set(url, (m.get(url) ?? 0) + count);
      }
    }
    return new Map([...m.entries()].sort((a, b) => b[1] - a[1]));
  }, [sends]);

  if (isLoading) return <LoaderScreen />;
  if (error || !campaign) {
    return (
      <Card>
        <EmptyState icon={AlertCircle}
          title="Could not load campaign"
          description={(error as { message?: string })?.message ?? 'Not found.'}
          action={<Link to="/newsletter/campaigns"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back</Button></Link>}
        />
      </Card>
    );
  }

  // Draft campaigns redirect to editor
  if (campaign.status === 'draft' && canWrite) {
    return (
      <Card>
        <EmptyState icon={Pencil}
          title="Campaign is still a draft"
          description="Finish composing and send it first — analytics will appear here after it's delivered."
          action={
            <Link to={`/newsletter/campaigns/${id}/edit`}>
              <Button leftIcon={<Pencil size={14} />}>Edit Campaign</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const m = campaign.metrics ?? {
    queued: 0, sent: 0, delivered: 0, opens: 0, uniqueOpens: 0,
    clicks: 0, uniqueClicks: 0, bounces: 0, complaints: 0, unsubscribes: 0,
  };

  const deliveryRate = m.sent > 0 ? (m.delivered / m.sent) * 100 : 0;
  const openRate     = m.delivered > 0 ? (m.uniqueOpens / m.delivered) * 100 : 0;
  const clickRate    = m.delivered > 0 ? (m.uniqueClicks / m.delivered) * 100 : 0;
  const bounceRate   = m.sent > 0 ? (m.bounces / m.sent) * 100 : 0;

  return (
    <div className="space-y-8">
      <Link to="/newsletter/campaigns" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted">
        <ArrowLeft size={12} /> <span className="kicker">All Campaigns</span>
      </Link>

      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="kicker">Campaign</span>
            <Badge tone={STATUS_TONES[campaign.status]}>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Badge>
          </div>
          <h1 className="font-display text-3xl text-text mb-2">{campaign.name}</h1>
          <p className="text-sm text-text-muted">{campaign.subject}</p>
          {campaign.sendStartedAt && (
            <div className="mt-3 flex items-center gap-2 text-xs text-text-dim">
              <Clock size={11} />
              Sent {new Date(campaign.sendStartedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
              {campaign.actualRecipientCount !== undefined && (
                <>
                  <span>·</span>
                  <Users size={11} />
                  {campaign.actualRecipientCount.toLocaleString()} recipients
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Sending progress (if still sending) */}
      {campaign.status === 'sending' && (
        <Card>
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="kicker mb-1">Sending in Progress</div>
              <div className="text-sm text-text">
                {m.sent.toLocaleString()} of {m.queued.toLocaleString()} sent
              </div>
            </div>
            <span className="font-display text-2xl text-brass-300 tabular">
              {m.queued > 0 ? ((m.sent / m.queued) * 100).toFixed(1) : 0}%
            </span>
          </div>
          <div className="h-2 bg-bg-subtle rounded-full overflow-hidden">
            <div className="h-full bg-brass-400 transition-all"
              style={{ width: `${m.queued > 0 ? (m.sent / m.queued) * 100 : 0}%` }} />
          </div>
        </Card>
      )}

      {/* Primary metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <MetricTile
          icon={Send}
          label="Delivered"
          primary={m.delivered.toLocaleString()}
          secondary={`${deliveryRate.toFixed(1)}% delivery rate`}
        />
        <MetricTile
          icon={Eye}
          label="Opens"
          primary={`${openRate.toFixed(1)}%`}
          secondary={`${m.uniqueOpens.toLocaleString()} unique · ${m.opens.toLocaleString()} total`}
        />
        <MetricTile
          icon={MousePointerClick}
          label="Clicks"
          primary={`${clickRate.toFixed(1)}%`}
          secondary={`${m.uniqueClicks.toLocaleString()} unique · ${m.clicks.toLocaleString()} total`}
        />
        <MetricTile
          icon={AlertTriangle}
          label="Bounces"
          primary={m.bounces.toLocaleString()}
          secondary={`${bounceRate.toFixed(2)}% bounce rate`}
          alert={bounceRate > 5}
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <SmallMetric label="Complaints"   value={m.complaints} alert={m.complaints > 0} />
        <SmallMetric label="Unsubscribes" value={m.unsubscribes} />
        <SmallMetric label="Failed"       value={m.sent === 0 ? 0 : Math.max(m.queued - m.sent - m.bounces, 0)} />
      </div>

      {/* URL click breakdown */}
      {urlClicks.size > 0 && (
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Clicked URLs</div>
            <p className="text-sm text-text-muted">Breakdown of which links recipients clicked.</p>
          </header>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-2 font-normal">URL</th>
                <th className="kicker px-6 py-2 font-normal text-right">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {[...urlClicks.entries()].slice(0, 20).map(([url, count]) => (
                <tr key={url} className="border-b border-border last:border-0">
                  <td className="px-6 py-2 font-mono text-xs text-text truncate max-w-xl">{url}</td>
                  <td className="px-6 py-2 text-right tabular text-text">{count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Per-recipient sends */}
      {sends && sends.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Recipients · {sends.length.toLocaleString()}</div>
            <p className="text-sm text-text-muted">Per-subscriber delivery and engagement status.</p>
          </header>
          <div className="max-h-[30rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg-panel">
                <tr className="border-b border-border text-left">
                  <th className="kicker px-6 py-2 font-normal">Email</th>
                  <th className="kicker px-6 py-2 font-normal">Status</th>
                  <th className="kicker px-6 py-2 font-normal">Error</th>
                  <th className="kicker px-6 py-2 font-normal text-right">Opens</th>
                  <th className="kicker px-6 py-2 font-normal text-right">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {sends.slice(0, 500).map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                    <td className="px-6 py-2 font-mono text-xs text-text truncate max-w-xs">{s.email}</td>
                    <td className="px-6 py-2">
                      <SendStatusBadge status={s.status} />
                    </td>
                    <td className="px-6 py-2 text-xs text-danger max-w-sm">
                      {(s as { failureReason?: string }).failureReason ?? <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-6 py-2 text-right tabular text-xs">
                      {s.opens > 0
                        ? <span className="text-text">{s.opens}</span>
                        : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-6 py-2 text-right tabular text-xs">
                      {s.clicks > 0
                        ? <span className="text-brass-300">{s.clicks}</span>
                        : <span className="text-text-dim">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sends.length > 500 && (
              <div className="p-4 text-center text-xs text-text-dim border-t border-border">
                Showing first 500 of {sends.length.toLocaleString()} recipients.
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function MetricTile({ icon: Icon, label, primary, secondary, alert }: {
  icon: typeof Send; label: string; primary: string; secondary: string; alert?: boolean;
}) {
  return (
    <div className="bg-bg-panel p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} className={alert ? 'text-warning' : 'text-text-dim'} />
        <span className="kicker">{label}</span>
      </div>
      <div className={`font-display text-3xl tabular ${alert ? 'text-warning' : 'text-text'}`}>
        {primary}
      </div>
      <div className="text-2xs text-text-dim mt-1">{secondary}</div>
    </div>
  );
}

function SmallMetric({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-1">{label}</div>
      <div className={`font-display text-xl tabular ${alert ? 'text-danger' : 'text-text'}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function SendStatusBadge({ status }: { status: string }) {
  const tones: Record<string, { tone: 'success' | 'neutral' | 'info' | 'brass' | 'danger' | 'warning'; icon?: typeof Send }> = {
    queued:       { tone: 'neutral' },
    sent:         { tone: 'info' },
    delivered:    { tone: 'success', icon: CheckCircle2 },
    opened:       { tone: 'brass', icon: Eye },
    clicked:      { tone: 'brass', icon: MousePointerClick },
    bounced:      { tone: 'danger', icon: AlertTriangle },
    complained:   { tone: 'danger' },
    unsubscribed: { tone: 'warning', icon: UserX },
    failed:       { tone: 'danger' },
  };
  const { tone, icon: Icon } = tones[status] ?? { tone: 'neutral' };
  return (
    <Badge tone={tone}>
      {Icon && <Icon size={9} />}
      {status}
    </Badge>
  );
}
